import styled from '@emotion/styled'
import DragEventService, { EventPosition } from 'drag-event-service'
import { memo, useCallback, useRef, useState } from 'react'
import PinnedRooms from '.'
import { pinRoom, showPinRoomChat } from '../../../../../actions/room'
import {
    getScreenState,
    setScreenOrientation,
} from '../../../../../app-state/state.screen'
import { openFindPinnedRooms } from '../../../../../state/modals/state.drawer'
import { APPSTATE } from '../../../../../state/state.app'
import { LAYOUTSTATE } from '../../../../../state/state.layout'
import { ROOMSTATE } from '../../../../../state/state.rooms'
import { isTouchDevice } from '../../../../../utilities/browser'
import ButtonVideoDrag from '../../../../Buttons/ButtonVideoDrag'
import IconButton from '../../../../Buttons/IconButton'
import { AppStateType, useAppState } from '../../../../Hooks/useAppState'
import { usePrevious } from '../../../../Hooks/usePrevious'
import { getPinnedRooms, usePinnedRooms } from '../../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../../Hooks/useScreenState'
import Collapse from '../../../../Icons/Collapse'
import Expand from '../../../../Icons/Expand'
import HideChat from '../../../../Icons/HideChat'
import Plus from '../../../../Icons/Plus'
import ShowChat from '../../../../Icons/ShowChat'
import { getThemeSpacing } from '../../../../styles/appTheme'
import { palette } from '../../../../styles/colors'
import shape from '../../../../styles/shape'
import {
    COMPONENT_WIDTHS,
    PINNED_ROOM_MIN_WIDTH_FOR_CHAT,
} from '../../../../theme'
import { PinDragHelper, hideDragHelper } from './PinDragHelper'

const PinContainerRoot = styled('div')({
    position: 'relative',
    containerName: 'pin-container',
    containerType: 'inline-size',
    transition: 'background-color 0.4s ease-in-out',
    borderRadius: shape.borderRadius,
})

const PinHelperRoot = styled('div')({
    position: 'absolute',
    bottom: 8,
    right: 8,
    zIndex: 1,
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    // width: 180,
    borderRadius: shape.borderRadiusLarge,
    flexFlow: 'row nowrap',
    alignItems: 'center',
    color: palette.commonWhite,
    background: palette.floatingToolbarBackgroundLight,
    // padding: `${getThemeSpacing(0.5)} ${getThemeSpacing(1)}`,
    // fontWeight: 800,
    // fontSize: '0.8rem',
    // '& span': {
    //     margin: `${getThemeSpacing(0.5)} ${getThemeSpacing(1)}`,
    // },
    '& button': {
        padding: 0,
    },
    '& svg': {
        width: 24,
        height: 24,
        color: palette.commonWhite,
        margin: `${getThemeSpacing(0.5)}`,
        cursor: 'pointer',
    },
})

const MIN_RATIO = 0.27
const MAX_MOBILE_RATIO = 0.67
const MAX_RATIO = 0.8

const maxPinnedRoomSize = () =>
    setScreenOrientation({ ratio: MIN_RATIO, overrideSkip: true })
const minPinnedRoomSize = () =>
    setScreenOrientation({
        ratio: APPSTATE.app.isMobile ? MAX_MOBILE_RATIO : MAX_RATIO,
        overrideSkip: true,
    })

interface PinHelperProps {
    showFindPins: boolean
    ratio: number
    width: number
}

const PinHelper = memo<PinHelperProps>(({ showFindPins, ratio, width }) => {
    // TODO: add a toggle for hiding pins (leaving a pin tab that hangs off the right edge?)
    const previousRatio = usePrevious(ratio)
    const [isExpanded, setIsExpanded] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const onClickExpand = useCallback(() => {
        if (!isExpanded) {
            maxPinnedRoomSize()
        } else {
            setScreenOrientation({ ratio: previousRatio, overrideSkip: true })
        }
        setIsExpanded((prev) => !prev)
    }, [isExpanded, previousRatio])
    const onClickCollapse = useCallback(() => {
        if (!isCollapsed) {
            minPinnedRoomSize()
        } else {
            setScreenOrientation({ ratio: previousRatio, overrideSkip: true })
        }
        setIsCollapsed((prev) => !prev)
    }, [isCollapsed, previousRatio])
    const onClickShowChat = useCallback(() => {
        const pinnedRooms = getPinnedRooms()
        pinnedRooms.forEach((id) => {
            showPinRoomChat(id)
        })
    }, [])
    const onClickHideChat = useCallback(() => {
        const pinnedRooms = getPinnedRooms()
        pinnedRooms.forEach((id) => {
            showPinRoomChat(id, false)
        })
    }, [])

    return (
        <PinHelperRoot>
            {showFindPins && (
                <IconButton
                    title="Find Pinned Rooms"
                    onClick={openFindPinnedRooms}
                    data-testid="PinRoomHelper__FindButton"
                >
                    <Plus />
                </IconButton>
            )}
            <IconButton
                title="Expand Pinned Rooms"
                data-testid="PinRoomHelper__ExpandButton"
                onClick={onClickExpand}
                disabled={ratio <= MIN_RATIO}
            >
                <Expand />
            </IconButton>
            <IconButton
                title="Collapse Pinned Rooms"
                data-testid="PinRoomHelper__CollapseButton"
                onClick={onClickCollapse}
                disabled={
                    ratio >=
                    (APPSTATE.app.isMobile ? MAX_MOBILE_RATIO : MAX_RATIO)
                }
            >
                <Collapse />
            </IconButton>
            {width >= PINNED_ROOM_MIN_WIDTH_FOR_CHAT && (
                <>
                    <IconButton
                        title="Show All Pinned Room Chat"
                        data-testid="PinRoomHelper__ShowChatButton"
                        onClick={onClickShowChat}
                    >
                        <ShowChat />
                    </IconButton>
                    <IconButton
                        title="Hide All Pinned Room Chat"
                        data-testid="PinRoomHelper__HideChatButton"
                        onClick={onClickHideChat}
                    >
                        <HideChat />
                    </IconButton>
                </>
            )}
        </PinHelperRoot>
    )
})

type PinContainerProps = {
    width: number
    pinLimit: number
    vertical: boolean
    singlePin: boolean
    hidePins: boolean
}

const PinContainer = memo(
    ({ width, singlePin, pinLimit, vertical, hidePins }: PinContainerProps) => {
        const dragThrottle = useRef<number>(0)

        const dragRef = useRef<HTMLButtonElement>(null)
        const pinContainerRootRef = useRef<HTMLDivElement>(null)
        const { darkMode, isIOS, isMobile } = useAppState(AppStateType.APP)
        const pinnedRooms = usePinnedRooms()
        const ratio = useScreenStateSelector('ratio')

        const isMobileDevice = isMobile || isIOS
        // landscape: pins should be on the side instead of at the top
        const sidePins = !vertical

        // Size divider button drag handlers
        const onSizeButtonDragEnd = useCallback(
            (event: MouseEvent | TouchEvent) => {
                event.stopPropagation()
                event.preventDefault()
                DragEventService.off(document, 'move', onSizeButtonDrag)
                DragEventService.off(document, 'end', onSizeButtonDragEnd)
            },
            [],
        )
        const onSizeButtonDrag = useCallback(
            (event: MouseEvent | TouchEvent, position: EventPosition) => {
                event.stopPropagation()
                event.preventDefault()
                const { pageX: x } = position
                const offsetX = !vertical
                    ? LAYOUTSTATE.layout.expandedNav
                        ? COMPONENT_WIDTHS.leftNavExpanded
                        : COMPONENT_WIDTHS.leftNav
                    : 0
                const { windowWidth } = getScreenState()
                const ratio = Math.max(
                    Math.min(
                        (x - offsetX) / windowWidth,
                        isMobile ? MAX_MOBILE_RATIO : MAX_RATIO,
                    ),
                    MIN_RATIO,
                )
                cancelAnimationFrame(dragThrottle.current)
                dragThrottle.current = requestAnimationFrame(() => {
                    setScreenOrientation({ ratio, overrideSkip: true })
                })
            },
            [],
        )
        const onSizeButtonDragStart = useCallback(
            (event: MouseEvent | TouchEvent) => {
                event.stopPropagation()
                event.preventDefault()
                DragEventService.on(document, 'move', onSizeButtonDrag, {
                    touchArgs: [{ passive: false }],
                })
                DragEventService.on(document, 'end', onSizeButtonDragEnd, {
                    touchArgs: [{ passive: false }],
                })
            },
            [],
        )
        const sizeButtonDragRefWrapper = useCallback(
            (ref: HTMLButtonElement) => {
                if (ref) {
                    dragRef.current = ref
                    DragEventService.on(ref, 'start', onSizeButtonDragStart, {
                        touchArgs: [{ passive: false }],
                    })
                } else {
                    if (dragRef.current) {
                        DragEventService.off(
                            dragRef.current,
                            'start',
                            onSizeButtonDragStart,
                        )
                    }
                }
            },
            [],
        )
        const dropPin = useCallback(() => {
            if (pinContainerRootRef.current) {
                pinContainerRootRef.current.style.backgroundColor = 'unset'
            }
            if (ROOMSTATE.pinHelperID) pinRoom(ROOMSTATE.pinHelperID)
            hideDragHelper()
        }, [])
        const dragOver = useCallback((event) => {
            if (pinContainerRootRef.current) {
                pinContainerRootRef.current.style.backgroundColor =
                    palette.layerSecondaryShadow
            }
            event.preventDefault()
            event.stopPropagation()
        }, [])
        const dragEnter = useCallback((event) => {
            if (pinContainerRootRef.current) {
                pinContainerRootRef.current.style.backgroundColor =
                    palette.layerSecondaryShadow
            }
            event.preventDefault()
        }, [])
        const dragLeave = useCallback((event) => {
            if (pinContainerRootRef.current) {
                pinContainerRootRef.current.style.backgroundColor = 'unset'
            }
            event.preventDefault()
        }, [])
        if (pinnedRooms.length < 1 || hidePins)
            return !isTouchDevice ? <PinDragHelper /> : null
        return (
            <>
                <PinContainerRoot
                    ref={pinContainerRootRef}
                    style={{ width }}
                    onDrop={dropPin}
                    onDragOver={dragOver}
                    onDragEnter={dragEnter}
                    onDragLeave={dragLeave}
                >
                    {!vertical && (
                        <ButtonVideoDrag ref={sizeButtonDragRefWrapper} />
                    )}
                    <PinnedRooms
                        darkMode={darkMode}
                        isMobile={isMobileDevice}
                        pinnedRooms={pinnedRooms}
                        roomWidth={width}
                        sidePins={sidePins}
                        singlePin={singlePin}
                    />
                    {!vertical && (
                        <PinHelper
                            showFindPins={pinLimit > pinnedRooms.length}
                            width={width}
                            ratio={ratio}
                        />
                    )}
                </PinContainerRoot>
                {!isTouchDevice && <PinDragHelper />}
            </>
        )
    },
)

export default PinContainer
